import gql from 'graphql-tag';
import { clients } from '../../util/clients';
import { bulkResult } from '../../util/shared/graphql-global';
import { bankVoucherDataHeaders } from '../../util/shared/tmc-global';
import { amountFormat, amountToNumber, fieldsToGqlString } from '../../util/shared/vue-global';

const { backend } = clients.direct;

export const ImportActions = {
  async upsertODTransactions({ dispatch, commit }, upserts, chunkId) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation upsertODTransactions($upserts: [ODTransactionBulkInput]) {
          upsertODTransactions(upserts:$upserts) {
            insertedCount, upsertedCount, matchedCount, deletedCount,
            result {ok, nInserted, nMatched, nRemoved, nUpserted, nModified }
          }
        }`,
        variables: {
          upserts,
        },
      });
      clients.handleGQLErrors(results, commit);

      return results;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', `Something went wrong in the import of chunk id ${chunkId}.`, { root: true });
    }
    return {};
  },
  async importBankVoucher({ dispatch, commit }, upserts, chunkId) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation importBankVoucher($upserts: [BankVoucherBulkInput]) {
          importBankVoucher(upserts:$upserts) {
            ${bulkResult}
          }
        }`,
        variables: {
          upserts,
        },
      });
      clients.handleGQLErrors(results, commit);

      return results;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', `Something went wrong in the import of chunk id ${chunkId}.`, { root: true });
    }
    return {};
  },
  async cleanupBankVoucher({ dispatch, commit }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation cleanupBankVoucher {
          cleanupBankVoucher {
            ${bulkResult}
          }
        }`,
      });
      return results;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'Something went wrong.', { root: true });
    }
    return {};
  },
  // Progress marker of a staged import run, so a results screen can tell a complete import from one
  // that is still loading or stopped partway. Resolves null for a run from before markers existed
  // and undefined when the check itself failed - callers must not read undefined as "complete".
  async loadImportRunStatus({ dispatch }, { chunkID, destination } = {}) {
    try {
      await dispatch('reAuth', null, { root: true });
      const results = await backend.query({
        query: gql`query importRunStatus($chunkID: String, $destination: String) {
          importRunStatus(chunkID: $chunkID, destination: $destination) {
            chunkID destination status expectedRows stagedRows finished error
          }
        }`,
        variables: { chunkID: chunkID || null, destination: destination || null },
        fetchPolicy: 'no-cache',
      });
      return results.data.importRunStatus;
    } catch (e) {
      dispatch('logException', e, { root: true });
    }
    return undefined;
  },
  // Marks every matched voucher of a completed import run paid on the server, so the count that
  // comes back is what the server confirmed rather than what the browser managed to send.
  async markBankVouchersPaid({ dispatch, commit }, { chunkID }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation markBankVouchersPaid($chunkID: String!) {
          markBankVouchersPaid(chunkID: $chunkID) {
            totalCount paidCount failedCount failedVouchers
          }
        }`,
        variables: { chunkID },
      });
      clients.handleGQLErrors(results, commit);
      return results.data.markBankVouchersPaid;
    } catch (e) {
      dispatch('logException', e, { root: true });
      // the server says why in plain words (still importing, import failed...), so show that
      const reason = String((e && e.message) || '').replace(/^(GraphQL error|Network error):\s*/i, '');
      dispatch('flashError', reason || 'Something went wrong marking the vouchers paid.', { root: true });
    }
    return undefined;
  },
  async loadBankVoucherData({ dispatch, commit }, { chunkID } = {}) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql` query loadBankVoucherData($chunkID: String) {
          bankVoucherTransactions(chunkID: $chunkID) {
            date voucher amount
            transactionDetails { id register registration fiscalYear
              departmentDetail { dept description }
            }
          }
        }`,
        variables: { chunkID: chunkID || null },
        fetchPolicy: 'no-cache',
      });
      const { bankVoucherTransactions } = results.data;
      const reducedHeader = [...bankVoucherDataHeaders];
      const processedFileData = {
        matchedData: {
          headers: [...bankVoucherDataHeaders],
          data: [],
          totalAmount: 0,
        },
        unmatchedData: {
          headers: reducedHeader.splice(3, 3),
          data: [],
          totalAmount: 0,
        },
      };

      for (let i = 0; i < bankVoucherTransactions.length; i += 1) {
        const trans = bankVoucherTransactions[i];
        if (trans.transactionDetails !== null) {
          processedFileData.matchedData.data.push(
            {
              voucher: trans.voucher,
              amount: amountFormat(trans.amount),
              date: trans.date,
              transactionId: trans.transactionDetails.id,
              register: trans.transactionDetails.register,
              registration: trans.transactionDetails.registration,
              fiscalYear: trans.transactionDetails.fiscalYear,
              deptName: trans.transactionDetails.departmentDetail.description,
              deptNo: trans.transactionDetails.departmentDetail.dept,
            },
          );
          processedFileData.matchedData.totalAmount += amountToNumber(trans.amount);
        } else {
          processedFileData.unmatchedData.data.push(
            {
              voucher: trans.voucher,
              amount: amountFormat(trans.amount),
              date: trans.date,
            },
          );
          processedFileData.unmatchedData.totalAmount += amountToNumber(trans.amount);
        }
      }
      // committed even when the run staged nothing, so the screen never keeps the last run's lists
      commit('setProcessedDetails', processedFileData);
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading the imported vouchers.', { root: true });
    }
  },
  async loadImportedPaidWarrants({ dispatch, commit }, {
    gqlfields, postProccess, chunkID,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql` query paidWarrantsTransactions($chunkID: String) {
          paidWarrantsTransactions(chunkID: $chunkID) {
            ${fieldsToGqlString(gqlfields)},
          }
        }`,
        variables: { chunkID: chunkID || null },
        fetchPolicy: 'no-cache',
      });
      const { paidWarrantsTransactions } = results.data;
      const processedFileData = postProccess({
        transData: (paidWarrantsTransactions || []),
      });
      commit('setProcessedDetails', processedFileData);
      // only on success: the caller tells "loaded, and nothing is staged" from "could not load"
      return processedFileData;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading imported paid warrants.', { root: true });
    }
    return undefined;
  },
};

export default ImportActions;
